import type { CSSProperties, JSX } from 'react';
import mapGrad from '../assets/images/error/map-grad.webp';

// Matches aosphere-nextgenui-user's --surface-page-from / --surface-page-to tokens.
const SURFACE_GRADIENT = 'linear-gradient(180deg, #8092a2 0%, #4e657f 100%)';
// Fades the map into a horizontal band (visible in the middle third, transparent at
// the very top/bottom) instead of a uniform tiled opacity.
const MAP_EDGE_FADE_MASK = 'linear-gradient(180deg, transparent 0%, #000 22%, #000 58%, transparent 86%)';
// Darkens the bottom of the viewport so footer text stays legible over the map.
const BOTTOM_DARKEN_GRADIENT =
  'linear-gradient(180deg, rgba(78,101,127,0) 0%, rgba(78,101,127,0.5) 30%, rgba(70,91,114,0.85) 60%, rgba(66,86,108,0.95) 100%)';

/**
 * Shared full-page background for the error/not-found pages — gradient + world-map
 * image, faded into a horizontal band with the bottom darkened for footer legibility.
 * Ported from aosphere-nextgenui-user's MapGradientBackground (error-page variant only
 * — the app-dashboard tiled/film-grain variant isn't used here). Renders absolutely
 * positioned layers, so the parent must be `position: relative` (or similar) and at
 * least as tall as the content on top.
 */
export function MapGradientBackground(): JSX.Element {
  const baseStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: SURFACE_GRADIENT,
  };

  const mapStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: `url(${mapGrad})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'auto',
    backgroundAttachment: 'fixed',
    WebkitMaskImage: MAP_EDGE_FADE_MASK,
    maskImage: MAP_EDGE_FADE_MASK,
  };

  const darkenStyle: CSSProperties = {
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    height: '55%',
    pointerEvents: 'none',
    backgroundImage: BOTTOM_DARKEN_GRADIENT,
  };

  return (
    <>
      <div aria-hidden="true" style={baseStyle} />
      <div aria-hidden="true" style={mapStyle} />
      <div aria-hidden="true" style={darkenStyle} />
    </>
  );
}
